import { txt } from '../utils/dom.js';
import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { altAttr, textoPlano } from '../utils/a11y.js';
import { atributosEncuadre } from '../utils/encuadre.js';

export const teamBlock = (rawProps) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const title = p.title || 'Nuestro Equipo';
    const subtitle = p.subtitle || 'Las personas detrás del proyecto';
    const members = p.members || [
        { name: 'Ana García', role: 'CEO & Fundadora', image: '', initials: 'AG' },
        { name: 'Carlos López', role: 'Director de Producto', image: '', initials: 'CL' },
        { name: 'Laura Martín', role: 'Lead Designer', image: '', initials: 'LM' },
        { name: 'Diego Sánchez', role: 'Head of Engineering', image: '', initials: 'DS' },
    ];

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // --- Avatar helper ---
    const renderAvatar = (m, size = 'default') => {
        // textoPlano (utils/a11y.js) es la MISMA limpieza que usa el alt: el nombre viene de un
        // contenteditable y puede traer marcado. Con él las iniciales tampoco salen de un "<b>".
        const cleanName = textoPlano(m.name);
        const defaultInitials = cleanName ? cleanName.split(' ').map(w => w[0]).join('').substring(0, 2) : 'A';
        if (m.image) {
            // La foto ES de esa persona: su nombre la describe (y sin alt el lector leería la URL).
            // Encuadre de la foto de esta persona (utils/encuadre.js, D-1452: «centrar caras»),
            // guardado en el miembro. Sin él, la <img> de siempre byte a byte.
            const enc = atributosEncuadre(m);
            return `<img src="${m.image}" ${altAttr(m.name)}${enc.clase ? ` class="${enc.clase.trim()}"` : ''} style="width:100%;height:100%;object-fit:cover;${enc.estilo}" loading="lazy">`;
        }
        // Sin foto, las iniciales son un adorno: el nombre completo está en el .team-name de al lado,
        // así que se ocultan al lector de pantalla en vez de hacerle deletrear "A G".
        return `<div class="team-initials" aria-hidden="true" style="background:color-mix(in srgb, var(--accent) 10%, transparent);color:var(--accent);">${m.initials || defaultInitials}</div>`;
    };

    // --- Toolbar helper ---
    const itemToolbar = (m, i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="members" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="members" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn image-picker-btn" data-array="members" data-index="${i}" title="Cambiar imagen" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.image}</button>
            ${m.image ? `<button class="tool-btn image-remove-btn" data-array="members" data-item-index="${i}" title="Quitar imagen" style="background: ${btnBg} !important; color: #fb7185 !important;"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>` : ''}
            <button class="tool-btn item-delete-btn" data-array="members" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // --- Encabezado compartido ---
    const header = `
        <div style="text-align:center; margin-bottom:70px;">
            <h2 data-field="title" contenteditable="true" style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${title}</h2>
            <p data-field="subtitle" contenteditable="true" style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${subtitle}</p>
        </div>`;

    // --- v1 (actual): grid de cards con avatar arriba + info abajo ---
    if (variant === 'v1' || (!variant || variant === '')) {
        return `
    <section id="bloque-team" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:100px 8%;">
        ${header}
        <div class="team-grid">${members.map((m, i) => `
            <div class="team-card stagger-item sortable-item" data-item-index="${i}" data-array-name="members" style="--stagger-i:${i}; position:relative;">
                ${itemToolbar(m, i)}
                <div class="team-avatar">${renderAvatar(m)}</div>
                <div class="team-info">
                    <h3 class="team-name" data-field="name" contenteditable="true">${txt(m.name)}</h3>
                    <p class="team-role" data-field="role" contenteditable="true" style="color:var(--accent);">${txt(m.role)}</p>
                </div>
            </div>`).join('')}
        </div>
    </section>`;
    }

    // --- v2 ("horizontal"): fila horizontal con avatar circular pequeño a la izquierda ---
    if (variant === 'v2') {
        return `
    <section id="bloque-team" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:100px 8%;">
        ${header}
        <div style="max-width: 800px; margin: 0 auto; display: flex; flex-direction: column; gap: 0;">
            ${members.map((m, i) => `
            <div class="sortable-item" data-item-index="${i}" data-array-name="members" 
                 style="display: flex; align-items: center; gap: 20px; padding: 24px 0; position: relative; ${i !== 0 ? 'border-top: 1px solid color-mix(in srgb, var(--text) 8%, transparent);' : ''}">
                ${itemToolbar(m, i)}
                <div style="width: 56px; height: 56px; border-radius: 50%; overflow: hidden; flex-shrink: 0; background: color-mix(in srgb, var(--accent) 10%, transparent);">
                    ${renderAvatar(m)}
                </div>
                <div style="flex: 1; min-width: 0;">
                    <h3 data-field="name" contenteditable="true" style="font-size: calc(1.1rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight: 700; margin: 0 0 4px;">${txt(m.name)}</h3>
                    <p data-field="role" contenteditable="true" style="color: var(--accent); font-size: calc(0.9rem * var(--text-scale, 1)); margin: 0; opacity: 0.8;">${txt(m.role)}</p>
                </div>
            </div>`).join('')}
        </div>
    </section>`;
    }

    // --- v3 ("spotlight"): primer miembro destacado grande + resto en grid compacto ---
    const [featured, ...rest] = members;
    return `
    <section id="bloque-team" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}'; padding:100px 8%;">
        ${header}
        <!-- Miembro destacado -->
        <div class="sortable-item" data-item-index="0" data-array-name="members" style="max-width: 500px; margin: 0 auto 60px; text-align: center; position: relative;">
            ${itemToolbar(featured, 0)}
            <div style="width: 120px; height: 120px; border-radius: 50%; overflow: hidden; margin: 0 auto 24px; border: 3px solid var(--accent); box-shadow: 0 8px 32px color-mix(in srgb, var(--accent) 20%, transparent); background: color-mix(in srgb, var(--accent) 10%, transparent);">
                ${renderAvatar(featured)}
            </div>
            <h3 data-field="name" contenteditable="true" style="font-size: calc(1.5rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight: 800; margin-bottom: 6px;">${txt(featured.name)}</h3>
            <p data-field="role" contenteditable="true" style="color: var(--accent); font-size: calc(1rem * var(--text-scale, 1)); font-weight: 600;">${txt(featured.role)}</p>
        </div>
        <!-- Resto del equipo en grid compacto -->
        ${rest.length > 0 ? `
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px; max-width: 900px; margin: 0 auto;">
            ${rest.map((m, i) => `
            <div class="sortable-item" data-item-index="${i + 1}" data-array-name="members" style="text-align: center; position: relative; padding: 20px 0;">
                ${itemToolbar(m, i + 1)}
                <div style="width: 64px; height: 64px; border-radius: 50%; overflow: hidden; margin: 0 auto 12px; background: color-mix(in srgb, var(--accent) 10%, transparent);">
                    ${renderAvatar(m)}
                </div>
                <h3 data-field="name" contenteditable="true" style="font-size: calc(1rem * var(--heading-scale, var(--u-type-scale, 1))); font-weight: 700; margin-bottom: 4px;">${txt(m.name)}</h3>
                <p data-field="role" contenteditable="true" style="color: var(--accent); font-size: calc(0.85rem * var(--text-scale, 1)); opacity: 0.8;">${txt(m.role)}</p>
            </div>`).join('')}
        </div>` : ''}
    </section>`;
};
